import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import { appFetchJson } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";
import EessMultiSelect from "../../../components/multiselect/EessMultiSelect";
import ProductoAutocomplete from "../../../components/autocomplete/ProductoAutocomplete";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

export default function CrearPrecioProductoEessFuturo({
  onClose = () => {},
  onCreated = () => {},
}) {

  // --- AYUDANTES DE FECHA ---
  
  // Fecha de HOY (para el campo fecha de registro/carga)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fecha de MAÑANA (para sugerir como inicio de vigencia)
  const getTomorrowDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // --- ESTADOS ---
  const [fechaVigencia, setFechaVigencia] = useState(getTomorrowDate());
  const [eessSel, setEessSel] = useState([]);
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [valorPrecio, setValorPrecio] = useState("");
  const [precioCompra, setPrecioCompra] = useState("");
  const [saving, setSaving] = useState(false);

  // Validaciones visuales
  const isValid = eessSel.length > 0 && selectedProducto !== null && fechaVigencia !== "";

  // Cerrar con tecla Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const guardar = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    try {
      // 1. Datos fijos para todos los registros
      const fechaCargaHoy = getTodayDate(); 

      // 2. Iterar sobre estaciones seleccionadas (Multi-insert)
      const promesas = eessSel.map(async (idEstacion) => {
        
        const payload = {
          eessId: idEstacion,
          productoId: selectedProducto?.id,
          // usuarioId: NO SE ENVÍA. El backend lo obtiene del Token de seguridad.
          
          fecha: fechaCargaHoy,            // Fecha en que se carga el dato (HOY)
          fechaVigenciaIni: fechaVigencia, // Fecha futura cuando entra en vigor

          precio: valorPrecio ? Number(valorPrecio) : 0,
          precio2: precioCompra ? Number(precioCompra) : 0 
        };

        return appFetchJson(apiRoutes.precioProductoEessFuturo.create, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      });

      await Promise.all(promesas);

      // 3. Finalizar
      if (onCreated) onCreated();
      onClose();

    } catch (e) {
      console.error("Error al guardar precio futuro:", e);
      alert(`Error al guardar: ${e.message || "Error desconocido en el servidor"}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Programar Precio Futuro</h2>
          <button className="modal-close-x" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="modal-body flex flex-col items-start">
          
          <p className="text-gray-600 mb-6 italic text-left w-full px-8">
            Establezca un precio que entrará en vigencia automáticamente en la fecha seleccionada.
          </p>

          {/* SECCIÓN SUPERIOR: CONFIGURACIÓN */}
          <div className="flex flex-col gap-3 mb-6 w-full px-8">
            {/* EE.SS. */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">EE.SS.*:</label>
              <div className="w-[600px]">
                <EessMultiSelect value={eessSel} onChange={setEessSel} className="w-full" />
              </div>
            </div>

            {/* FECHA VIGENCIA */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold text-blue-700">Inicio Vigencia*:</label>
              <div className="w-[600px]">
                <input
                  type="date"
                  // BLOQUEO: Esto impide seleccionar hoy o días pasados
                  min={getTomorrowDate()} 
                  className="input w-full h-[38px] bg-white border border-blue-300 rounded px-3 focus:border-blue-500"
                  value={fechaVigencia}
                  onChange={(e) => setFechaVigencia(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1 text-left">
                  El precio se activará a las 00:00 hs de esta fecha.
                </p>
              </div>
            </div>
          </div>

          {/* SEPARADOR VISUAL */}
          <div className="flex mb-0 w-full"> 
            <button className="bg-green-700 text-white px-6 py-2 rounded-t-md font-medium text-sm">
              Valores del Producto
            </button>
          </div>

          {/* CONTENEDOR GRIS INFERIOR: DATOS DEL PRECIO */}
          <div className="bg-gray-100 p-8 rounded-b-md rounded-tr-md space-y-5 w-full">
            
            {/* Producto */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">Producto*:</label>
              <div className="w-[600px]">
                <ProductoAutocomplete value={selectedProducto} onChange={setSelectedProducto} className="w-full" />
              </div>
            </div>

            {/* Precio Venta */}
            <div className="flex items-center mt-6">
              <label className="w-32 shrink-0 text-left font-bold">Valor Precio*:</label>
              <div className="w-[600px]">
                <InputNumberNoScroll
                  className="input w-full h-[38px] bg-white border rounded px-3"
                  value={valorPrecio}
                  onChange={(e) => setValorPrecio(e.target.value)}
                  placeholder="Ej: 7500"
                />
              </div>
            </div>

            {/* Precio Compra */}
            <div className="flex items-center">
              <label className="w-32 shrink-0 text-left font-bold">Precio Compra:</label>
              <div className="w-[600px]">
                <InputNumberNoScroll
                  className="input w-full h-[38px] bg-white border rounded px-3"
                  value={precioCompra}
                  onChange={(e) => setPrecioCompra(e.target.value)}
                  placeholder="Ej: 7100"
                />
              </div>
            </div>

          </div>
        </div>

        {/* FOOTER: ACCIONES */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar label="Programar Precio" isValid={isValid} saving={saving} onClick={guardar} />
            <BtnCancelar onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}